import { useState } from "react";
import { Link } from "react-router-dom";
import { Mail, Phone, MapPin, ArrowRight } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";

export default function Contact() {
  const [form, setForm] = useState({
    business_name: "",
    contact_person: "",
    email: "",
    phone: "",
    site_address: "",
    message: "",
  });
  const [submitting, setSubmitting] = useState(false);

  function upd(k) {
    return (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  }

  async function submit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/api/contact", form);
      toast.success("Thanks — we'll be in touch shortly.");
      setForm({ business_name: "", contact_person: "", email: "", phone: "", site_address: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <section className="container-x mx-auto max-w-7xl pt-14 md:pt-24 pb-16 grid lg:grid-cols-[1fr_1fr] gap-14" data-testid="contact-hero">
        <div>
          <p className="text-forest text-xs tracking-[0.18em] uppercase font-semibold mb-4">Contact</p>
          <h1 className="font-heading text-4xl md:text-6xl font-semibold text-forest leading-[1.03] tracking-tight">
            Talk to Be Green Group.
          </h1>
          <p className="mt-6 text-neutral-600 text-lg leading-relaxed max-w-lg">
            Whether you’re exploring a PPA or want to see if your site fits, we’re happy to walk
            through the model with you.
          </p>
          <ul className="mt-10 space-y-4 text-[15px] text-neutral-700">
            <li className="flex items-center gap-3"><span className="h-9 w-9 rounded-xl bg-mist grid place-items-center text-forest"><Mail size={16}/></span> hello@begreengroup.com.au</li>
            <li className="flex items-center gap-3"><span className="h-9 w-9 rounded-xl bg-mist grid place-items-center text-forest"><Phone size={16}/></span> 1300 000 000</li>
            <li className="flex items-center gap-3"><span className="h-9 w-9 rounded-xl bg-mist grid place-items-center text-forest"><MapPin size={16}/></span> Australia</li>
          </ul>

          <div className="mt-12 rounded-2xl bg-mist border border-black/5 p-6">
            <p className="text-[13px] uppercase tracking-widest text-forest font-semibold">Prefer to apply now?</p>
            <p className="mt-2 text-[14.5px] text-neutral-600 max-w-md">
              The full application takes about 10 minutes and lets our team assess your business
              energy fit accurately.
            </p>
            <Link to="/apply" className="mt-4 inline-flex items-center gap-2 text-forest font-semibold">
              Start online application <ArrowRight size={16}/>
            </Link>
          </div>
        </div>

        <form
          onSubmit={submit}
          className="bg-white rounded-3xl border border-black/5 p-7 md:p-10 shadow-sm"
          data-testid="contact-form"
        >
          <div className="grid gap-4">
            <Field label="Business name">
              <Input required value={form.business_name} onChange={upd("business_name")} data-testid="contact-business-name" placeholder="Legal or trading name" />
            </Field>
            <Field label="Contact person">
              <Input required value={form.contact_person} onChange={upd("contact_person")} data-testid="contact-person" placeholder="Full name" />
            </Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Email">
                <Input required type="email" value={form.email} onChange={upd("email")} data-testid="contact-email" placeholder="you@company.com" />
              </Field>
              <Field label="Phone">
                <Input value={form.phone} onChange={upd("phone")} data-testid="contact-phone" placeholder="Optional" />
              </Field>
            </div>
            <Field label="Site address">
              <Input value={form.site_address} onChange={upd("site_address")} data-testid="contact-site-address" placeholder="Optional" />
            </Field>
            <Field label="Message">
              <Textarea required value={form.message} onChange={upd("message")} data-testid="contact-message" rows={5} placeholder="Tell us a bit about your business and what you'd like to explore." />
            </Field>
          </div>
          <Button
            type="submit"
            disabled={submitting}
            data-testid="contact-submit"
            className="mt-6 w-full h-12 rounded-full bg-forest hover:bg-forest/90 text-white font-semibold"
          >
            {submitting ? "Sending..." : "Send message"}
          </Button>
        </form>
      </section>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-[13px] font-semibold text-forest">{label}</span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
